import React, { useEffect, useRef, useState } from "react";
import BaseTextField from "../../components/ui/BaseTextField";
import * as yup from "yup";
import { useFormik } from "formik";
import BaseButton from "../../components/ui/BaseButton";
import { Link } from "react-router-dom";
import { forgotPassword } from "../../services/api/auth";
import { useMutation } from "@tanstack/react-query";
import { showToast } from "../../store/toast/toastSlice";
import { useDispatch } from "react-redux";

import { ReactComponent as CheckedIcon } from "../../assets/global-icons/link-send-success.svg";

const validationSchema = yup.object({
  email: yup
    .string("Enter your email")
    .email("Enter a valid email")
    .required("Email is required"),
});

const COOLDOWN = 60;
const LS_FORGOT_END = "forgotEndTime";

const ForgotPasswordForm = () => {
  const dispatch = useDispatch();
  const [isRecoveryEmailSent, setIsRecoveryEmailSent] = useState(false);
  const [timeLeft, setTimeLeft] = useState(0);
  const intervalRef = useRef(null);

  const {
    isPending: isForgotPasswordLoading,
    error: isForgotPasswordError,
    data: forgotPasswordData,
    mutateAsync: forgotPasswordMutation,
  } = useMutation({
    mutationFn: forgotPassword,
  });
  useEffect(() => {
    const end = Number(localStorage.getItem(LS_FORGOT_END) || 0);
    const now = Math.floor(Date.now() / 1000);
    if (end > now) setTimeLeft(end - now);

    if (intervalRef.current) window.clearInterval(intervalRef.current);
    intervalRef.current = window.setInterval(() => {
      setTimeLeft((s) => {
        if (s <= 1) {
          if (intervalRef.current) window.clearInterval(intervalRef.current);
          intervalRef.current = null;
          localStorage.removeItem(LS_FORGOT_END);
          return 0;
        }
        return s - 1;
      });
    }, 1000);

    return () => {
      if (intervalRef.current) window.clearInterval(intervalRef.current);
      intervalRef.current = null;
    };
  }, []);

  const startCooldown = (secs) => {
    const end = Math.floor(Date.now() / 1000) + secs;
    localStorage.setItem(LS_FORGOT_END, String(end));
    setTimeLeft(secs);
  };

  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s < 10 ? "0" : ""}${s}`;
  };
  const hydrateCooldown = () => {
    const end = Number(localStorage.getItem(LS_FORGOT_END) || 0);
    const now = Math.floor(Date.now() / 1000);
    const remaining = end > now ? end - now : 0;

    setTimeLeft(remaining);
    if (remaining === 0) localStorage.removeItem(LS_FORGOT_END);

    if (!intervalRef.current && remaining > 0) {
      intervalRef.current = window.setInterval(() => {
        setTimeLeft((s) => {
          if (s <= 1) {
            if (intervalRef.current) window.clearInterval(intervalRef.current);
            intervalRef.current = null;
            localStorage.removeItem(LS_FORGOT_END);
            return 0;
          }
          return s - 1;
        });
      }, 1000);
    }
  };
  const onSubmit = async (payload) => {
    try {
      const forgotPasswordResponse = await forgotPasswordMutation(payload);
      const sent =
        forgotPasswordResponse && forgotPasswordResponse.sent === true;
      setIsRecoveryEmailSent(sent);

      if (sent) {
        const cooldown =
          typeof forgotPasswordResponse.cooldownRemaining === "number" &&
          forgotPasswordResponse.cooldownRemaining > 0
            ? forgotPasswordResponse.cooldownRemaining
            : COOLDOWN;
        startCooldown(cooldown);
      } else if (forgotPasswordResponse && forgotPasswordResponse.message) {
        dispatch(
          showToast({
            type: "error",
            style: {
              width: "350px",
            },
            position: "top-center",
            message: forgotPasswordResponse?.message,
          })
        );
      }
    } catch (error) {
      const msg =
        error?.response?.data?.message ||
        "Failed to send recovery email. Please try again.";
      dispatch(
        showToast({
          position: "top-center",
          type: "error",
          message: Array.isArray(msg) ? msg[0] : msg,
        })
      );
    }
  };

  const formik = useFormik({
    initialValues: { email: "" },
    validationSchema,
    onSubmit,
    validateOnChange: true,
    validateOnBlur: false,
  });

  const disabled = isForgotPasswordLoading || timeLeft > 0;
  const label =
    timeLeft > 0
      ? `Resend in ${formatTime(timeLeft)}`
      : isForgotPasswordLoading
      ? "Sending..."
      : "Send recovery link";

  return (
    <form onSubmit={formik.handleSubmit}>
      {isRecoveryEmailSent ? (
        <div>
          <p className="text-base text-center mb-4 px-6 mt-8">
            If an account exists for the email below, a password recovery email
            with instructions has been sent. Please follow the instructions to
            reset the password.
          </p>
          {formik.values.email && (
            <p className="text-center text-base font-500 mb-8 font-semibold">
              {formik.values.email}
            </p>
          )}
          <div className="flex justify-center ">
            <CheckedIcon />
          </div>
          <div className="flex mt-8 justify-between px-10">
            <Link to="/login" className="text-primary text-base">
              Return to login
            </Link>

            <p
              className="text-primary text-base cursor-pointer"
              onClick={() => {
                hydrateCooldown();
                setIsRecoveryEmailSent(false);
              }}
            >
              Return to Recovery Link
            </p>
          </div>
        </div>
      ) : (
        <div className="mt-8">
          <h4 className="font-semibold text-[#525354] text-center mb-4">
            Forgot Password ?
          </h4>
          <p className="text-base text-center mb-4 px-9 mt-4">
            Provide your Registered E-mail address and we’ll send you a password
            reset link.
          </p>
          <BaseTextField
            height="60px"
            className="mb-10 mt-8"
            placeholder="William123@gmail.com"
            endAdornment={
              <i class="fa-regular fa-envelope text-base text-gray-400"></i>
            }
            id="email"
            name="email"
            label="Email"
            value={formik.values.email}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.email && Boolean(formik.errors.email)}
            helperText={formik.touched.email && formik.errors.email}
            InputLabelProps={{
              shrink: true, // <-- keeps label always on top
            }}
            // className="mb-6"
          />

          <div className="flex flex-col items-center">
            <BaseButton
              color="primary"
              loaderSize="20px"
              isLoading={isForgotPasswordLoading}
              type="submit"
              className="mt-5 text-base py-[8px] rounded-full"
              fullWidth={true}
              disabled={disabled}
            >
              {label}
            </BaseButton>
            <Link to="/login" className="text-secondary mt-5 text-sm w-full">
              <BaseButton
                variant="outlined"
                color="primary"
                loaderSize="20px"
                className="text-base py-[8px] rounded-full  border-primary "
                fullWidth={true}
              >
                Return to Login
              </BaseButton>
            </Link>
          </div>
        </div>
      )}
    </form>
  );
};

export default ForgotPasswordForm;
